import React, { useEffect, useRef, useState } from "react";
import { fxStyle } from "@/lib/mediaFx";
import { ChevronsLeftRight, Play } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { imageVariant, resolveMediaUrl } from "@/lib/mediaUrl";
import { isHlsSrc, posterFor, attachHls } from "@/lib/videoSource";
import { useFeedAttachSlot } from "@/components/VideoPlayer";
import { SoundControl } from "@/components/SoundControl";
import { NEUTRAL_POSTER } from "@/lib/neutralPoster";

// « Avant / Après » — 2 photos : curseur de comparaison (on glisse la barre pour révéler l'avant / l'après) ;
// 2 vidéos : côte à côte, la 1re (Avant) joue d'abord puis la 2e (Après) enchaîne, chacune avec son propre son.
const cardWidth = () => Math.min(typeof window !== "undefined" ? window.innerWidth : 640, 640);

const Label = ({ children, side, testId }) => (
  <span className={`absolute top-2 ${side === "left" ? "left-2" : "right-2"} z-[3] px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide text-white pointer-events-none`}
    style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }} data-testid={testId}>
    {children}
  </span>
);

// Cadre : dimensions connues (bornées 4:5 … 1.91:1), sinon 4:5.
const frameRatio = (items) => {
  const m = items.find((x) => x.width > 0 && x.height > 0);
  return m ? Math.min(1.91, Math.max(0.8, m.width / m.height)) : 0.8;
};

export const BeforeAfterSlider = ({ post, items, onOpen, testId, className = "" }) => {
  const { t } = useApp();
  const [pos, setPos] = useState(50);
  const [hint, setHint] = useState(true);
  const boxRef = useRef(null);
  const drag = useRef(null);
  const tid = testId || `post-before-after-${post?.id}`;
  const [before, after] = items;
  const setFromX = (clientX) => {
    const r = boxRef.current?.getBoundingClientRect();
    if (!r || !r.width) return;
    setPos(Math.max(2, Math.min(98, ((clientX - r.left) / r.width) * 100)));
  };
  const onDown = (e) => {
    if (e.button != null && e.button !== 0) return;
    drag.current = { x0: e.clientX, moved: false };
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* noop */ }
  };
  const onMove = (e) => {
    const g = drag.current;
    if (!g) return;
    if (!g.moved && Math.abs(e.clientX - g.x0) < 4) return;
    g.moved = true;
    setHint(false);
    setFromX(e.clientX);
  };
  const onUp = (e) => {
    const g = drag.current;
    drag.current = null;
    if (!g || g.moved) return;
    // Tap sans glisser → visionneuse, sur la photo touchée (gauche = avant, droite = après).
    const r = boxRef.current?.getBoundingClientRect();
    const right = r && r.width ? ((e.clientX - r.left) / r.width) * 100 > pos : false;
    onOpen?.(right ? 1 : 0);
  };
  return (
    <div ref={boxRef}
      className={`relative w-full overflow-hidden select-none bg-black/5 ${className}`}
      style={{ aspectRatio: frameRatio(items), maxHeight: "min(72vh, 640px)", touchAction: "pan-y", cursor: "ew-resize" }}
      onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={() => { drag.current = null; }}
      data-testid={tid} data-kind="image" data-pos={Math.round(pos)}>
      {/* Lot 679 — variante choisie sur le plus grand côté affiché (cadre = largeur / ratio, ≤ 640 px) : paysage → 864 sur téléphone. */}
      <img src={imageVariant(after.url, cardWidth(), { cssHeight: Math.min(640, cardWidth() / frameRatio(items)) })} alt="" draggable={false} className="absolute inset-0 w-full h-full object-cover" data-testid={`${tid}-after`} />
      <img src={imageVariant(before.url, cardWidth(), { cssHeight: Math.min(640, cardWidth() / frameRatio(items)) })} alt="" draggable={false} className="absolute inset-0 w-full h-full object-cover" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }} data-testid={`${tid}-before`} />
      <Label side="left" testId={`${tid}-label-before`}>{t("post.before", "Avant")}</Label>
      <Label side="right" testId={`${tid}-label-after`}>{t("post.after", "Après")}</Label>
      <div className="absolute inset-y-0 z-[2] w-0.5 bg-white pointer-events-none" style={{ left: `calc(${pos}% - 1px)`, boxShadow: "0 0 6px rgba(0,0,0,0.5)" }} aria-hidden />
      <div className="absolute top-1/2 z-[3] -translate-y-1/2 -translate-x-1/2 h-10 w-10 rounded-full grid place-items-center bg-white shadow-lg pointer-events-none" style={{ left: `${pos}%`, color: "#2B2B2B" }} data-testid={`${tid}-handle`}>
        <ChevronsLeftRight size={18} />
      </div>
      {hint && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-[3] px-2.5 py-1 rounded-full text-[11px] font-semibold text-white pointer-events-none whitespace-nowrap" style={{ background: "rgba(0,0,0,0.55)" }} data-testid={`${tid}-hint`}>
          {t("post.before_after_drag", "Glisse pour comparer")}
        </div>
      )}
    </div>
  );
};

export const BeforeAfterVideos = ({ post, items, onOpen, testId, className = "" }) => {
  const { t } = useApp();
  const tid = testId || `post-before-after-${post?.id}`;
  const r0 = useRef(null);
  const r1 = useRef(null);
  const refs = [r0, r1];
  const boxRef = useRef(null);
  const visibleRef = useRef(false);
  const activeRef = useRef(0);
  const [active, setActive] = useState(0);
  const [muted, setMuted] = useState([true, true]);
  const attached = useFeedAttachSlot(boxRef);
  const posters = items.map((m) => posterFor(m.url, m.poster_url ? imageVariant(m.poster_url, 480) : m.poster_url) || NEUTRAL_POSTER);

  useEffect(() => {
    if (!attached) return undefined;
    const cleanups = items.map((m, i) => {
      const el = refs[i].current;
      if (!el) return null;
      if (isHlsSrc(m.url)) return attachHls(el, m.url, { autoPlay: () => visibleRef.current && activeRef.current === i });
      el.src = resolveMediaUrl(m.url);
      if (visibleRef.current && activeRef.current === i) el.play().catch(() => {});
      return () => { try { el.removeAttribute("src"); el.load(); } catch { /* noop */ } };
    });
    return () => cleanups.forEach((c) => c && c());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attached, items[0]?.url, items[1]?.url]);

  const play = (i) => {
    activeRef.current = i;
    setActive(i);
    const other = refs[1 - i].current;
    if (other) other.pause();
    const el = refs[i].current;
    if (!el || !visibleRef.current) return;
    try { el.currentTime = 0; } catch { /* noop */ }
    el.play().catch(() => {});
  };

  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(([e]) => {
      visibleRef.current = !!e.isIntersecting;
      if (e.isIntersecting) refs[activeRef.current].current?.play().catch(() => {});
      else refs.forEach((r) => r.current?.pause());
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { refs.forEach((r, i) => { if (r.current) r.current.muted = muted[i]; }); }, [muted]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div ref={boxRef} className={`grid grid-cols-2 gap-1 ${className}`} data-testid={tid} data-kind="video" data-active={active}>
      {items.map((m, i) => (
        <div key={i} className="relative aspect-[3/4] overflow-hidden bg-black" data-testid={`${tid}-${i}`} data-playing={active === i ? "1" : "0"}>
          <div className="absolute inset-0 pointer-events-none" aria-hidden
            style={{ backgroundImage: `url(${posters[i]})`, backgroundSize: "cover", backgroundPosition: "center", filter: "blur(24px)", transform: "scale(1.2)", opacity: 0.75 }} />
          <video ref={refs[i]} poster={posters[i]} muted={muted[i]} playsInline preload="metadata"
            onEnded={() => play(1 - i)}
            onClick={() => onOpen?.(i)}
            className="relative z-[1] w-full h-full object-contain cursor-pointer" style={fxStyle(m.fx)} data-testid={`${tid}-video-${i}`} />
          <Label side="left" testId={`${tid}-label-${i}`}>{i === 0 ? t("post.before", "Avant") : t("post.after", "Après")}</Label>
          {active !== i && (
            <button type="button" onClick={() => play(i)} className="absolute inset-0 z-[2] grid place-items-center" style={{ background: "rgba(0,0,0,0.25)" }} aria-label={t("post.album_open", "Ouvrir")} data-testid={`${tid}-play-${i}`}>
              <span className="h-11 w-11 rounded-full grid place-items-center text-white" style={{ background: "rgba(0,0,0,0.55)" }}><Play size={20} className="ml-0.5" /></span>
            </button>
          )}
          <SoundControl muted={muted[i]} onToggle={() => setMuted((m) => m.map((v, k) => (k === i ? !v : v)))} variant="compact" className="absolute bottom-2 right-2 z-20"
            noAudioLabel={t("video.no_sound", "Sans son")} muteLabel={t("video.mute", "Couper le son")} hintLabel={t("video.tap_for_sound", "Appuyez pour le son")} testId={`${tid}-sound-${i}`} />
        </div>
      ))}
    </div>
  );
};

// Rendu « Avant / Après » d'une publication (photos → curseur, vidéos → côte à côte) ; null si la mise en page ne s'applique pas.
export const BeforeAfterBlock = ({ post, ba, onOpen, className = "" }) => {
  if (!ba) return null;
  return (
    <div className={`mt-3 rounded-xl overflow-hidden ${className}`} style={{ border: "1px solid var(--yalola-border)" }} data-testid={`post-before-after-wrap-${post?.id}`}>
      {ba.kind === "video" ? <BeforeAfterVideos post={post} items={ba.items} onOpen={onOpen} /> : <BeforeAfterSlider post={post} items={ba.items} onOpen={onOpen} />}
    </div>
  );
};

// Curseur « Avant / Après » AUTONOME (carte partagée dans une story) : la barre fait un lent va-et-vient toute seule
// (≈ 3 s par aller) ; le doigt la déplace aussi, l'animation reprend 2,5 s après. Vidéos → leurs affiches sont comparées.
// `compact` (vignette de la rangée des stories) : figé à 50 %, sans animation ni labels. Respecte prefers-reduced-motion.
const AUTO_AMPL = 35;
const AUTO_SPEED = Math.PI / 3; // rad/s → période 6 s = 3 s par aller
const reducedMotion = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const BeforeAfterAuto = ({ items = [], kind = "image", animate = true, compact = false, labels, testId = "before-after-auto", className = "", style }) => {
  const { t } = useApp();
  const boxRef = useRef(null);
  const beforeRef = useRef(null);
  const barRef = useRef(null);
  const handleRef = useRef(null);
  const posRef = useRef(50);
  const rafRef = useRef(0);
  const dragRef = useRef(null);
  const movedRef = useRef(false);
  const resumeRef = useRef(null);
  const showLabels = labels ?? !compact;
  const live = animate && !compact && !reducedMotion();
  const srcs = items.map((m) => (kind === "video"
    ? (posterFor(m.url, m.poster_url ? imageVariant(m.poster_url, 640) : m.poster_url) || NEUTRAL_POSTER)
    : imageVariant(m.url, compact ? 240 : 640)));

  const apply = (pos) => {
    posRef.current = pos;
    if (beforeRef.current) beforeRef.current.style.clipPath = `inset(0 ${100 - pos}% 0 0)`;
    if (barRef.current) barRef.current.style.left = `calc(${pos}% - 1px)`;
    if (handleRef.current) handleRef.current.style.left = `${pos}%`;
    boxRef.current?.setAttribute("data-pos", String(Math.round(pos)));
  };
  const stop = () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); rafRef.current = 0; };
  const start = () => {
    if (!live || rafRef.current) return;
    const phase = Math.asin(Math.max(-1, Math.min(1, (posRef.current - 50) / AUTO_AMPL)));
    const t0 = performance.now();
    const step = (now) => {
      apply(50 + AUTO_AMPL * Math.sin(phase + ((now - t0) / 1000) * AUTO_SPEED));
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  };
  useEffect(() => {
    apply(50);
    start();
    boxRef.current?.setAttribute("data-animating", live ? "1" : "0");
    return () => { stop(); clearTimeout(resumeRef.current); if (dragRef.current?.moved) window.dispatchEvent(new CustomEvent("yalola:story-hold", { detail: { held: false } })); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live]);

  const onDown = (e) => {
    if (compact || (e.button != null && e.button !== 0)) return;
    dragRef.current = { x0: e.clientX, moved: false };
    clearTimeout(resumeRef.current);
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* noop */ }
  };
  const onMove = (e) => {
    const g = dragRef.current;
    if (!g) return;
    if (!g.moved && Math.abs(e.clientX - g.x0) < 4) return;
    if (!g.moved) { g.moved = true; stop(); boxRef.current?.setAttribute("data-animating", "0"); window.dispatchEvent(new CustomEvent("yalola:story-hold", { detail: { held: true } })); }
    const r = boxRef.current?.getBoundingClientRect();
    if (r?.width) apply(Math.max(2, Math.min(98, ((e.clientX - r.left) / r.width) * 100)));
  };
  const onUp = () => {
    const g = dragRef.current;
    dragRef.current = null;
    if (!g?.moved) return;
    movedRef.current = true;
    window.dispatchEvent(new CustomEvent("yalola:story-hold", { detail: { held: false } }));
    resumeRef.current = setTimeout(() => { start(); boxRef.current?.setAttribute("data-animating", live ? "1" : "0"); }, 2500);
  };
  // Un glissement ne doit pas compter comme un tap (la carte hôte ouvre la publication au clic).
  const onClick = (e) => { if (movedRef.current) { movedRef.current = false; e.preventDefault(); e.stopPropagation(); } };
  if (items.length !== 2) return null;
  return (
    <div ref={boxRef} className={`relative w-full overflow-hidden select-none bg-black/10 ${className}`}
      style={{ aspectRatio: frameRatio(items), touchAction: compact ? undefined : "pan-y", ...style }}
      onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} onClickCapture={onClick}
      data-testid={testId} data-kind={kind} data-pos="50">
      <img src={srcs[1]} alt="" draggable={false} className="absolute inset-0 w-full h-full object-cover" data-testid={`${testId}-after`} />
      <img ref={beforeRef} src={srcs[0]} alt="" draggable={false} className="absolute inset-0 w-full h-full object-cover" style={{ clipPath: "inset(0 50% 0 0)" }} data-testid={`${testId}-before`} />
      {showLabels && <Label side="left" testId={`${testId}-label-before`}>{t("post.before", "Avant")}</Label>}
      {showLabels && <Label side="right" testId={`${testId}-label-after`}>{t("post.after", "Après")}</Label>}
      <div ref={barRef} className={`absolute inset-y-0 z-[2] bg-white pointer-events-none ${compact ? "w-px" : "w-0.5"}`} style={{ left: "calc(50% - 1px)", boxShadow: "0 0 6px rgba(0,0,0,0.5)" }} aria-hidden />
      {!compact && (
        <div ref={handleRef} className="absolute top-1/2 z-[3] -translate-y-1/2 -translate-x-1/2 h-9 w-9 rounded-full grid place-items-center bg-white shadow-lg pointer-events-none" style={{ left: "50%", color: "#2B2B2B" }} data-testid={`${testId}-handle`}>
          <ChevronsLeftRight size={16} />
        </div>
      )}
    </div>
  );
};
